import { Badge, Card, CardContent, toast } from '@estac1/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '../../lib/api.js';
import { NumberField, ValetPriceField } from './billing-form-fields.js';
import { HardwareModulesSection } from './hardware-modules-section.js';
import { NfsePricingSection } from './nfse-pricing-section.js';
import type { BillingFlags } from './types.js';

/**
 * Catalog modules: flat (valet, avarias, OCR), usage (SMS, cobrança online), free (e-mail).
 * Prices live in platform_settings; free modules have no price field.
 */
export const ModulesCatalogCard = () => {
  const qc = useQueryClient();
  const flags = useQuery({
    queryKey: ['admin-settings-flags'],
    queryFn: () => api.request<BillingFlags>('/v1/admin/settings/flags'),
  });

  const update = useMutation({
    mutationFn: (patch: Record<string, unknown>) =>
      api.request<BillingFlags>('/v1/admin/settings/flags', { method: 'PUT', json: patch }),
    onSuccess: () => {
      toast.success('Preços dos módulos atualizados.');
      qc.invalidateQueries({ queryKey: ['admin-settings-flags'] });
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Falha ao salvar.'),
  });

  const f = flags.data;

  return (
    <Card>
      <CardContent className="space-y-4 pt-6">
        <div>
          <h2 className="text-sm font-semibold">Módulos do catálogo</h2>
          <p className="text-xs text-muted-fg">
            Preços padrão dos módulos que o dono ativa no painel. Plano por vaga fica na tabela de
            faixas acima. Módulos grátis só têm gate (sem valor na fatura).
          </p>
        </div>
        {flags.isLoading || !f ? (
          <p className="text-sm text-muted-fg">Carregando...</p>
        ) : (
          <div className="space-y-6">
            <div>
              <p className="mb-2 text-sm font-medium">Mensalidade fixa</p>
              <div className="grid gap-3 sm:grid-cols-2">
                <ValetPriceField
                  label="Valet (mensal)"
                  valueCents={f.valetPricing.priceCents}
                  disabled={update.isPending}
                  onSave={(cents) => update.mutate({ valetPriceCents: cents })}
                />
                <ValetPriceField
                  label="Fotos de avaria (mensal)"
                  valueCents={f.damagePhotosPricing?.priceCents ?? 2490}
                  disabled={update.isPending}
                  onSave={(cents) => update.mutate({ damagePhotosPriceCents: cents })}
                />
                <ValetPriceField
                  label="Leitura de placa / OCR (mensal)"
                  valueCents={f.plateOcrPricing?.priceCents ?? 1990}
                  disabled={update.isPending}
                  onSave={(cents) => update.mutate({ plateOcrPriceCents: cents })}
                />
                <ValetPriceField
                  label="Convênio com lojas (mensal)"
                  valueCents={f.convenioPricing?.priceCents ?? 1990}
                  disabled={update.isPending}
                  onSave={(cents) => update.mutate({ convenioPriceCents: cents })}
                />
              </div>
            </div>
            <div className="border-t border-border pt-4">
              <p className="mb-2 text-sm font-medium">WhatsApp e SMS — revenda por mensagem</p>
              <p className="mb-3 text-xs text-muted-fg">
                Ativar o módulo é grátis; o dono escolhe o canal, e cada envio bem-sucedido entra na
                fatura pelo mesmo preço nos dois canais. O WhatsApp só aparece para o dono com a
                chave WhatsApp ligada e os segredos da Meta no Worker.
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                <ValetPriceField
                  label="Preço de revenda por mensagem"
                  valueCents={f.smsPricing.sellPriceCents}
                  disabled={update.isPending}
                  onSave={(cents) => update.mutate({ smsSellPriceCents: cents })}
                />
                <NumberField
                  label="Teto diário por lote"
                  hint="Anti-abuso: bloqueia envio ao atingir o teto."
                  value={f.smsDailyCapPerLot}
                  disabled={update.isPending}
                  onSave={(v) => update.mutate({ smsDailyCapPerLot: v })}
                  unit="SMS/dia"
                  min={1}
                  max={5000}
                />
              </div>
            </div>
            <div className="border-t border-border pt-4">
              <p className="mb-2 text-sm font-medium">Cobrança online — por cobrança confirmada</p>
              <p className="mb-3 text-xs text-muted-fg">
                Sem mensalidade. Cada Pix ou cartão pago no link (valet, gorjeta, mensalidade) entra
                na fatura do mês seguinte. Fecha no caixa não conta.
              </p>
              <ValetPriceField
                label="Taxa de serviço por cobrança"
                valueCents={f.checkoutPricing?.sellPriceCents ?? 49}
                disabled={update.isPending}
                onSave={(cents) => update.mutate({ checkoutSellPriceCents: cents })}
              />
            </div>
            <NfsePricingSection
              pricing={f.nfsePricing}
              disabled={update.isPending}
              onSave={(patch) => update.mutate(patch)}
            />
            {f.hardwareModules && f.hardwareModules.length > 0 && (
              <HardwareModulesSection
                modules={f.hardwareModules}
                disabled={update.isPending}
                onSaveMonthly={(key, cents) =>
                  update.mutate(
                    key === 'barrier' ? { barrierPriceCents: cents } : { printerPriceCents: cents },
                  )
                }
                onSaveSetup={(key, cents) =>
                  update.mutate(
                    key === 'barrier' ? { barrierSetupCents: cents } : { printerSetupCents: cents },
                  )
                }
              />
            )}
            <div className="border-t border-border pt-4">
              <p className="mb-2 text-sm font-medium">Módulos grátis (só gate)</p>
              <ul className="space-y-2 text-sm">
                {(
                  f.freeModules ?? [{ key: 'email', label: 'E-mail', note: 'E-mail transacional' }]
                ).map((m) => (
                  <li
                    key={m.key}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border px-3 py-2"
                  >
                    <span className="font-medium">{m.label}</span>
                    <span className="text-xs text-muted-fg">{m.note}</span>
                    <Badge variant="default">R$ 0</Badge>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
